import React, { useState, useEffect, useCallback } from 'react';
import { salesApi, branchApi, warrantyPlanApi, formatApiErrorDetail, useAuth, uploadApi } from '../../lib/api';
import { formatCurrency, formatDate, downloadCSV } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Pagination } from '../../components/Pagination';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  ShoppingCart, Loader2, Download, Search, ShieldCheck, ShieldX, CheckCircle, XCircle, Clock, Eye, FileText, User, Smartphone, Paperclip,
} from 'lucide-react';
import { toast } from 'sonner';

export default function SalesListPage() {
  const { user } = useAuth();
  const isShopOwner = user?.role === 'SHOP_OWNER';
  const [sales, setSales] = useState([]);
  const [branches, setBranches] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [branchFilter, setBranchFilter] = useState('all');
  const [warrantyFilter, setWarrantyFilter] = useState('all');
  const [approvalFilter, setApprovalFilter] = useState('all');
  const [selectedSale, setSelectedSale] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 0 });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (branchFilter !== 'all') params.branch_id = branchFilter;
      if (warrantyFilter === 'yes') params.warranty_attached = true;
      if (warrantyFilter === 'no') params.warranty_attached = false;
      const [salesRes, branchesRes, plansRes] = await Promise.all([
        salesApi.list(params), branchApi.list(), warrantyPlanApi.list(),
      ]);
      const salesData = salesRes.data;
      setSales(salesData.items || salesData);
      setPagination({ total: salesData.total || 0, pages: salesData.pages || 0 });
      const branchData = branchesRes.data;
      setBranches(branchData.items || branchData);
      setPlans(plansRes.data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to load sales');
    } finally {
      setLoading(false);
    }
  }, [page, branchFilter, warrantyFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async (saleId) => {
    try {
      await salesApi.approve(saleId);
      toast.success('Sale approved');
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to approve');
    }
  };

  const handleReject = async (saleId) => {
    if (!window.confirm('Reject this sale? This cannot be undone.')) return;
    try {
      await salesApi.reject(saleId);
      toast.success('Sale rejected');
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to reject');
    }
  };

  const handleExportCSV = () => {
    const exportData = filteredSales.map(sale => ({
      Date: formatDate(sale.sale_date),
      Branch: getBranchName(sale.branch_id),
      Customer: sale.customer_name, Phone: sale.customer_phone,
      Brand: sale.device_brand, Model: sale.device_model,
      IMEI: sale.device_imei, 'Device Price': sale.device_price,
      Warranty: sale.warranty_attached ? 'Yes' : 'No',
      'Plan Price': getPlanBasePrice(sale),
      'Sold Price': getWarrantyPrice(sale),
      Certificate: sale.warranty_certificate || '-',
      Status: sale.approval_status || 'APPROVED',
    }));
    downloadCSV(exportData, `sales-${formatDate(new Date())}`);
    toast.success('Sales exported to CSV');
  };

  const getBranchName = (branchId) => branches.find(b => b.id === branchId)?.branch_name || 'Unknown';
  const getPlanName = (planId) => plans.find(p => p.id === planId)?.plan_name || '-';
  const getPlanBasePrice = (sale) => {
    if (!sale.warranty_attached || !sale.warranty_plan_id) return '-';
    const plan = plans.find(p => p.id === sale.warranty_plan_id);
    return plan ? formatCurrency(plan.warranty_price) : '-';
  };
  const getWarrantyPrice = (sale) => {
    if (!sale.warranty_attached || !sale.warranty_plan_id) return '-';
    // Show override price if set, otherwise plan base price
    if (sale.warranty_price_override) return formatCurrency(sale.warranty_price_override);
    const plan = plans.find(p => p.id === sale.warranty_plan_id);
    return plan ? formatCurrency(plan.warranty_price) : '-';
  };

  const getApprovalBadge = (status) => {
    if (status === 'PENDING') return <Badge className="bg-amber-50 text-amber-700 border-amber-200 border"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
    if (status === 'REJECTED') return <Badge className="bg-red-50 text-red-700 border-red-200 border"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
    return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
  };

  const filteredSales = sales.filter(sale => {
    if (approvalFilter !== 'all' && (sale.approval_status || 'APPROVED') !== approvalFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return sale.customer_name.toLowerCase().includes(q) || sale.customer_phone.includes(q) ||
        sale.device_imei.includes(q) || sale.device_brand.toLowerCase().includes(q) || sale.device_model.toLowerCase().includes(q);
    }
    return true;
  });

  const pendingCount = sales.filter(s => s.approval_status === 'PENDING').length;

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Sales</h1>
            <p className="text-slate-600 mt-1">
              {filteredSales.length} records
              {pendingCount > 0 && isShopOwner && <span className="ml-2 text-amber-600 font-medium">({pendingCount} pending approval)</span>}
            </p>
          </div>
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-button">
            <Download className="h-4 w-4 mr-2" />Export CSV
          </Button>
        </div>

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Search customer, IMEI, device..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 h-11" data-testid="search-sales" />
              </div>
              {branches.length > 0 && (
                <Select value={branchFilter} onValueChange={(v) => { setBranchFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-full sm:w-48" data-testid="branch-filter"><SelectValue placeholder="Branch" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.branch_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Select value={warrantyFilter} onValueChange={(v) => { setWarrantyFilter(v); setPage(1); }}>
                <SelectTrigger className="w-full sm:w-40" data-testid="warranty-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Warranty: Yes</SelectItem>
                  <SelectItem value="no">Warranty: No</SelectItem>
                </SelectContent>
              </Select>
              {isShopOwner && (
                <Select value={approvalFilter} onValueChange={setApprovalFilter}>
                  <SelectTrigger className="w-full sm:w-40" data-testid="approval-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="PENDING">Pending</SelectItem>
                    <SelectItem value="APPROVED">Approved</SelectItem>
                    <SelectItem value="REJECTED">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" /></div>
            ) : filteredSales.length > 0 ? (
              <>
                <Table>
                <TableHeader>
                  <TableRow className="border-slate-100">
                    <TableHead>Date</TableHead>
                    <TableHead>Branch</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead>Warranty</TableHead>
                    <TableHead className="text-right">Plan Price</TableHead>
                    <TableHead className="text-right">Sold Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSales.map(sale => (
                    <TableRow key={sale.id} className="table-row-hover">
                      <TableCell className="text-slate-600">{formatDate(sale.sale_date)}</TableCell>
                      <TableCell className="text-slate-600">{getBranchName(sale.branch_id)}</TableCell>
                      <TableCell>
                        <p className="font-medium text-slate-900">{sale.customer_name}</p>
                        <p className="text-sm text-slate-500">{sale.customer_phone}</p>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-slate-900">{sale.device_brand} {sale.device_model}</p>
                        <p className="text-xs text-slate-500 font-mono">{sale.device_imei}</p>
                      </TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(sale.device_price)}</TableCell>
                      <TableCell>
                        <Badge className={sale.warranty_attached
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 border'
                          : 'bg-slate-100 text-slate-600 border-slate-200 border'
                        }>
                          {sale.warranty_attached ? 'Yes' : 'No'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm text-slate-600">{getPlanBasePrice(sale)}</TableCell>
                      <TableCell className="text-right text-sm font-medium text-[#005DE8]">{getWarrantyPrice(sale)}</TableCell>
                      <TableCell>{getApprovalBadge(sale.approval_status || 'APPROVED')}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setSelectedSale(sale)} className="h-8 w-8 p-0" data-testid={`view-${sale.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {isShopOwner && sale.approval_status === 'PENDING' && (
                            <>
                              <Button size="sm" onClick={() => handleApprove(sale.id)} className="h-7 bg-emerald-600 hover:bg-emerald-700 text-xs px-2" data-testid={`approve-${sale.id}`}>Approve</Button>
                              <Button size="sm" variant="outline" onClick={() => handleReject(sale.id)} className="h-7 text-red-600 border-red-200 text-xs px-2" data-testid={`reject-${sale.id}`}>Reject</Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
                <Pagination page={page} pages={pagination.pages} total={pagination.total} onPageChange={setPage} />
              </>
            ) : (
              <div className="text-center py-16">
                <ShoppingCart className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No sales found</h3>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sale Detail Dialog - Improved */}
        <Dialog open={!!selectedSale} onOpenChange={() => setSelectedSale(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Sale Details</DialogTitle>
            </DialogHeader>
            {selectedSale && (
              <div className="space-y-4 py-2">
                {/* Header info */}
                <div className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3">
                  <div className="text-sm">
                    <span className="text-slate-500">Date:</span> <span className="font-medium">{formatDate(selectedSale.sale_date)}</span>
                  </div>
                  <div>{getApprovalBadge(selectedSale.approval_status || 'APPROVED')}</div>
                </div>

                {/* Customer Section */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="h-4 w-4 text-[#005DE8]" />
                    <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Customer</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-slate-500">Name:</span> <span className="font-medium">{selectedSale.customer_name}</span></div>
                    <div><span className="text-slate-500">Phone:</span> <span className="font-medium">{selectedSale.customer_phone}</span></div>
                    {selectedSale.customer_email && <div className="col-span-2"><span className="text-slate-500">Email:</span> <span className="font-medium">{selectedSale.customer_email}</span></div>}
                  </div>
                </div>

                {/* Device Section */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Smartphone className="h-4 w-4 text-[#005DE8]" />
                    <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Device</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-slate-500">Brand:</span> <span className="font-medium">{selectedSale.device_brand}</span></div>
                    <div><span className="text-slate-500">Model:</span> <span className="font-medium">{selectedSale.device_model}</span></div>
                    <div><span className="text-slate-500">IMEI:</span> <span className="font-mono text-xs">{selectedSale.device_imei}</span></div>
                    <div><span className="text-slate-500">Price:</span> <span className="font-medium">{formatCurrency(selectedSale.device_price)}</span></div>
                  </div>
                </div>

                {/* Warranty Section */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck className="h-4 w-4 text-[#005DE8]" />
                    <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Warranty</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-slate-500">Attached:</span>
                      <Badge className={`ml-2 ${selectedSale.warranty_attached ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {selectedSale.warranty_attached ? 'Yes' : 'No'}
                      </Badge>
                    </div>
                    {selectedSale.warranty_plan_id && <div><span className="text-slate-500">Plan:</span> <span className="font-medium">{getPlanName(selectedSale.warranty_plan_id)}</span></div>}
                    {selectedSale.warranty_plan_id && <div><span className="text-slate-500">Plan Price:</span> <span className="font-medium">{getPlanBasePrice(selectedSale)}</span></div>}
                    {selectedSale.warranty_plan_id && <div><span className="text-slate-500">Sold Price:</span> <span className="font-medium text-[#005DE8]">{getWarrantyPrice(selectedSale)}</span></div>}
                    {selectedSale.warranty_certificate && <div><span className="text-slate-500">Certificate:</span> <span className="font-mono font-medium text-[#005DE8]">{selectedSale.warranty_certificate}</span></div>}
                    {selectedSale.warranty_override_reason && <div className="col-span-2"><span className="text-slate-500">Override Reason:</span> <p className="text-sm mt-1 text-slate-700">{selectedSale.warranty_override_reason}</p></div>}
                  </div>
                </div>

                {/* Branch & Attachments */}
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-500">Branch:</span>
                    <span className="font-medium">{getBranchName(selectedSale.branch_id)}</span>
                  </div>
                  {selectedSale.attachments?.length > 0 && (
                    <div className="flex items-center gap-1">
                      <Paperclip className="h-3.5 w-3.5 text-slate-400" />
                      <span className="text-slate-500">{selectedSale.attachments.length} attachment(s)</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
